import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useMemo } from 'react';
import { ArrowLeft, Building2, Droplets, Gauge, Layers, MapPin, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAgriSafeSoverdiDashboard } from '../hooks/useAgriSafeSoverdiDashboard';

const formatArea = (value: number): string =>
  `${value.toLocaleString(i18n.language, { maximumFractionDigits: 2 })} ha`;

const formatCapacity = (value: number | null): string =>
  value === null
    ? '—'
    : `${value.toLocaleString(i18n.language, { maximumFractionDigits: 2 })} m³/h`;

const irrigationTypeLabel = (value: string): string => {
  if (value === 'drip') return i18n.t("sectorViews.Goutte_a_goutte");
  return value || '—';
};

const SoverdiFarmSettingsR7: React.FC = () => {
  useTranslation();
  const navigate = useNavigate();
  const { topology, sectors: legacySectors, operationalSectors, bindingsUnavailable, loading, loadError } = useAgriSafeSoverdiDashboard();

  const sectors = operationalSectors ?? legacySectors;
  const inventoryUnavailable = bindingsUnavailable && topology?.sites.some(site => site.name === 'Roubia');
  const sectorsBySite = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const sector of sectors) {
      const names = map.get(sector.siteName) ?? [];
      names.push(sector.displayName);
      map.set(sector.siteName, names);
    }
    return map;
  }, [sectors]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="farm-settings-loading">
        <div className="w-10 h-10 rounded-full border-2 border-[var(--color-primary)] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!topology) {
    return (
      <div className="space-y-4" data-testid="farm-settings-unavailable">
        <button type="button" onClick={() => navigate('/r6-soverdi-settings')} className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-primary)]">
          <ArrowLeft className="h-4 w-4" /> {i18n.t("settings")}</button>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-6 text-sm text-[var(--text-secondary)]">
          {i18n.t("sectorViews.Les_informations_de_l_exploitation_sont_indisponibles_Aucune_valeur_de")}</div>
      </div>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="farm-settings-r7">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <button type="button" onClick={() => navigate('/r6-soverdi-settings')} className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-primary)]">
            <ArrowLeft className="h-4 w-4" /> {i18n.t("settings")}</button>
          <div className="mt-4 flex items-center gap-2 text-[var(--color-primary)]">
            <Building2 className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">{i18n.t("exploitation")}</span>
          </div>
          <h2 className="mt-1 text-xl font-semibold text-[var(--text-primary)] sm:text-2xl">{topology.clientName}</h2>
          <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
            {i18n.t("sectorViews.Structure_metier_de_l_exploitation_et_des_sites_actuellement_raccordes")}</p>
        </div>
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-[var(--border-light)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)]">
          <ShieldCheck className="h-3.5 w-3.5" /> {i18n.t("readOnlyShort")}</span>
      </section>

      {loadError && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" role="status">
          {i18n.t("sectorViews.Certaines_donnees_complementaires_sont_indisponibles_La_structure_cano")}</section>
      )}

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Surface")}</p>
          <p className="mt-2 text-xl font-semibold text-[var(--text-primary)]">{formatArea(topology.summary.areaHa)}</p>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Sites")}</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">{topology.summary.siteCount}</p>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Secteurs")}</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">{inventoryUnavailable ? i18n.t("availabilityUnavailable") : sectors.length}</p>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Parcelles")}</p>
          <p className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">{topology.summary.parcelCount}</p>
        </div>
        <div className="col-span-2 rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4 lg:col-span-1">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{i18n.t("sectorViews.Irrigation")}</p>
          <p className="mt-2 text-lg font-semibold text-[var(--text-primary)]">{irrigationTypeLabel(topology.irrigationType)}</p>
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">{i18n.t("sectorViews.Sites_raccordes")}</h3>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">{i18n.t("sectorViews.Donnees_structurelles_connues_pour_chaque_site")}</p>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {topology.sites.map((site) => {
            const parcelCount = site.sectors.reduce((sum, sector) => sum + sector.parcels.length, 0);
            const businessNames = sectorsBySite.get(site.name) ?? [];
            return (
              <article key={site.name} className="overflow-hidden rounded-2xl border border-[var(--border-light)] bg-[var(--surface)]" data-testid={`farm-settings-site-${site.name}`}>
                <div className="border-b border-[var(--border-light)] px-5 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-[var(--color-primary)]" />
                        <h4 className="font-semibold text-[var(--text-primary)]">{site.name}</h4>
                      </div>
                      <p className="mt-1 pl-6 text-sm text-[var(--text-secondary)]">{site.waterSource || '—'}</p>
                    </div>
                    <span className="rounded-full bg-[var(--gray-100)] px-2.5 py-1 text-xs font-medium text-[var(--text-secondary)]">
                      {bindingsUnavailable && site.name === 'Roubia' ? i18n.t("availabilityUnavailable") : i18n.t("sectorViews.sectorCount", { count: sectors.filter(sector => sector.siteName === site.name).length })}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 px-5 py-4 text-sm">
                  <div>
                    <div className="flex items-center gap-1.5 text-[var(--text-tertiary)]"><MapPin className="h-3.5 w-3.5" /> {i18n.t("sectorViews.Surface")}</div>
                    <p className="mt-1 font-semibold text-[var(--text-primary)]">{formatArea(site.areaHa)}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 text-[var(--text-tertiary)]"><Gauge className="h-3.5 w-3.5" /> {i18n.t("sectorViews.Capacite")}</div>
                    <p className="mt-1 font-semibold text-[var(--text-primary)]">{formatCapacity(site.capacityM3h)}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 text-[var(--text-tertiary)]"><Droplets className="h-3.5 w-3.5" /> {i18n.t("sectorViews.Parcelles")}</div>
                    <p className="mt-1 font-semibold text-[var(--text-primary)]">{parcelCount}</p>
                  </div>
                </div>

                <div className="border-t border-[var(--border-light)] px-5 py-4">
                  <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
                    <Layers className="h-3.5 w-3.5" /> {i18n.t("sectorViews.Secteurs")}</div>
                  {businessNames.length === 0 ? (
                    <p className="text-sm text-[var(--text-tertiary)]">{i18n.t("sectorViews.Aucun_nom_metier_disponible")}</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {businessNames.map((name) => (
                        <span key={name} className="rounded-lg bg-[var(--gray-100)] px-2.5 py-1.5 text-xs font-medium text-[var(--text-secondary)]">{name}</span>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="rounded-xl border border-[var(--border-light)] bg-[var(--gray-50)] p-4 text-sm text-[var(--text-secondary)]">
        {i18n.t("sectorViews.La_structure_technique_les_identifiants_internes_et_les_raccordements_")}</section>
    </div>
  );
};

export default SoverdiFarmSettingsR7;
